import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { BarChart3, CreditCard, Download, Loader2, Pencil, Plus, Trash2, Wallet } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const PAYMENT_TYPES = ['CB', 'Virement', 'Chèque', 'Espèce'];

const PAYMENT_COLORS = {
  CB: 'bg-blue-500',
  Virement: 'bg-indigo-500',
  Espèce: 'bg-emerald-500'
};

export const ChargesPage = () => {
  const { api } = useAuth();
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [exportPdfLoading, setExportPdfLoading] = useState(false);
  const [period, setPeriod] = useState('daily');
  const [chartMetric, setChartMetric] = useState('amount');
  const [stats, setStats] = useState(null);
  const [selectedBarIndex, setSelectedBarIndex] = useState(null);
  const [barDetailLoading, setBarDetailLoading] = useState(false);
  const [barDetail, setBarDetail] = useState(null);

  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [chargeForm, setChargeForm] = useState({
    description: '',
    amount: '',
    payment_type: 'CB',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    photo_url: '',
  });
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(null);

  const fetchCharges = async () => {
    const response = await api.get('/charges');
    setCharges(response.data || []);
  };

  const fetchStats = async (selectedPeriod = period) => {
    setStatsLoading(true);
    try {
      const response = await api.get(`/charges/stats?period=${selectedPeriod}`);
      setStats(response.data);
      setSelectedBarIndex(null);
      setBarDetail(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement des statistiques des charges');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchCharges(), fetchStats(period)]);
      } catch (error) {
        toast.error('Impossible de charger la page Charges');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    fetchStats(period);
  }, [period]);

  const resetChargeForm = () => {
    setChargeForm({
      description: '',
      amount: '',
      payment_type: 'CB',
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      photo_url: '',
    });
    setEditingCharge(null);
  };

  const openCreateChargeDialog = () => {
    resetChargeForm();
    setChargeDialogOpen(true);
  };

  const openEditChargeDialog = (charge) => {
    setEditingCharge(charge);
    setChargeForm({
      description: charge.description || '',
      amount: charge.amount?.toString() || '',
      payment_type: charge.payment_type || 'CB',
      payment_date: charge.payment_date ? format(parseISO(charge.payment_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      photo_url: charge.photo_url || '',
    });
    setChargeDialogOpen(true);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setChargeForm((prev) => Object.assign({}, prev, { photo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCharge = async (event) => {
    event.preventDefault();

    if (!chargeForm.description || !chargeForm.amount || !chargeForm.payment_date) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    const parsedAmount = parseFloat(chargeForm.amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }

    const payload = {
      description: chargeForm.description,
      amount: parsedAmount,
      payment_type: chargeForm.payment_type,
      payment_date: new Date(`${chargeForm.payment_date}T12:00:00`).toISOString(),
      photo_url: chargeForm.photo_url || null,
    };

    try {
      if (editingCharge) {
        await api.put(`/charges/${editingCharge.id}`, payload);
        toast.success('Charge mise à jour');
      } else {
        await api.post('/charges', payload);
        toast.success('Charge ajoutée');
      }

      setChargeDialogOpen(false);
      resetChargeForm();
      await Promise.all([fetchCharges(), fetchStats(period)]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la sauvegarde de la charge');
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!window.confirm('Supprimer cette charge ?')) {
      return;
    }

    try {
      await api.delete(`/charges/${chargeId}`);
      toast.success('Charge supprimée');
      await Promise.all([fetchCharges(), fetchStats(period)]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const triggerBrowserDownload = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    setExportExcelLoading(true);
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      const excelResponse = await api.get('/charges/export/excel', { responseType: 'blob' });
      triggerBrowserDownload(excelResponse.data, `charges_complet_${timestamp}.xlsx`);
      toast.success('Export Charges Excel téléchargé');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de l’export Charges Excel');
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleExportPdfDetailed = async () => {
    setExportPdfLoading(true);
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      const pdfResponse = await api.get('/charges/export/pdf', { responseType: 'blob' });
      triggerBrowserDownload(pdfResponse.data, `charges_detaille_${timestamp}.pdf`);
      toast.success('Export Charges PDF détaillé téléchargé');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de l’export Charges PDF');
    } finally {
      setExportPdfLoading(false);
    }
  };

  const trend = stats?.trend || [];
  let maxTrendValue = 0;
  for (let index = 0; index < trend.length; index += 1) {
    const value = chartMetric === 'amount'
      ? Number(trend[index].amount || 0)
      : Number(trend[index].count || 0);
    if (value > maxTrendValue) {
      maxTrendValue = value;
    }
  }

  const paymentTypes = stats?.payment_types || [];

  // Filter charges list by selected bar period if any, otherwise by current calendar month
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  const rangeStart = barDetail?.start_date ? new Date(barDetail.start_date) : defaultStart;
  const rangeEnd = barDetail?.end_date ? new Date(barDetail.end_date) : defaultEnd;
  const filteredCharges = charges.filter((charge) => {
    if (!charge.payment_date) return false;
    const d = new Date(charge.payment_date);
    return d >= rangeStart && d <= rangeEnd;
  });
  const currentMonthLabel = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const listLabel = barDetail
    ? `Période sélectionnée — ${barDetail.label} (${new Date(barDetail.start_date).toLocaleDateString('fr-FR')} → ${new Date(barDetail.end_date).toLocaleDateString('fr-FR')})`
    : `Mois en cours — ${currentMonthLabel}`;

  const handleClearBarSelection = () => {
    setSelectedBarIndex(null);
    setBarDetail(null);
  };

  const handleSelectBar = async (index) => {
    setSelectedBarIndex(index);
    setBarDetailLoading(true);
    try {
      const response = await api.get(`/charges/bar-details?period=${period}&index=${index}`);
      setBarDetail(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement du détail de période');
      setBarDetail(null);
    } finally {
      setBarDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="charges-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#065F46]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="charges-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900" data-testid="charges-page-title">Charge</h1>
          <p className="text-slate-500 text-sm md:text-base" data-testid="charges-page-subtitle">Suivi des paiements sortants par période</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-slate-300 hover:bg-slate-50"
            onClick={handleExportExcel}
            disabled={exportExcelLoading}
            data-testid="charge-export-excel-button"
          >
            {exportExcelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exporter Excel
          </Button>
          <Button
            type="button"
            className="bg-[#065F46] hover:bg-[#047857] text-white"
            onClick={handleExportPdfDetailed}
            disabled={exportPdfLoading}
            data-testid="charge-export-pdf-button"
          >
            {exportPdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF détaillé
          </Button>
          <Button
            type="button"
            className="bg-[#065F46] hover:bg-[#047857] text-white"
            onClick={openCreateChargeDialog}
            data-testid="charge-add-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une charge
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="border border-slate-200" data-testid="charges-total-count-card">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500">Nombre de charges</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="charges-total-count-value">{stats?.total_charges || 0}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200" data-testid="charges-total-amount-card">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500">Montant total</p>
            <p className="text-3xl font-bold text-[#065F46] mt-1" data-testid="charges-total-amount-value">{Number(stats?.total_amount || 0).toFixed(2)} €</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-slate-200" data-testid="charges-chart-card">
        <CardHeader className="border-b border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="font-outfit text-lg" data-testid="charges-chart-title">Diagramme des charges</CardTitle>
            <div className="flex items-center gap-1 p-1 rounded-md bg-slate-100" data-testid="charges-metric-switch">
              <button
                type="button"
                className={`px-2 py-1 text-xs rounded transition-colors ${chartMetric === 'amount' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                onClick={() => setChartMetric('amount')}
                data-testid="charges-metric-amount"
              >
                Montant
              </button>
              <button
                type="button"
                className={`px-2 py-1 text-xs rounded transition-colors ${chartMetric === 'count' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                onClick={() => setChartMetric('count')}
                data-testid="charges-metric-count"
              >
                Nombre
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="charges-period-selector">
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'daily' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('daily')}
              data-testid="charges-period-daily"
            >
              Journalier
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'weekly' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('weekly')}
              data-testid="charges-period-weekly"
            >
              Hebdomadaire
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'monthly' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('monthly')}
              data-testid="charges-period-monthly"
            >
              Mensuel
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'yearly' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('yearly')}
              data-testid="charges-period-yearly"
            >
              Annuel
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {statsLoading ? (
            <div className="py-12 flex items-center justify-center" data-testid="charges-stats-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto" data-testid="charges-chart-wrapper">
                <div className="min-w-[700px] flex items-end gap-1" data-testid="charges-chart">
                  {trend.map((point, index) => {
                    const value = chartMetric === 'amount' ? Number(point.amount || 0) : Number(point.count || 0);
                    const barHeight = maxTrendValue > 0 ? Math.max((value / maxTrendValue) * 100, 6) : 0;
                    const valueLabel = chartMetric === 'amount' ? `${value.toFixed(0)}€` : `${value}`;
                    const isActive = selectedBarIndex === index;
                    return (
                      <div key={`${point.label}-${index}`} className="flex-1 min-w-0 flex flex-col items-center gap-1" data-testid={`charges-chart-point-${index}`}>
                        <p className="text-[10px] text-slate-500" data-testid={`charges-chart-value-${index}`}>{valueLabel}</p>
                        <div className="h-24 w-full flex items-end justify-center">
                          <div
                            className={`w-5/6 rounded-t bg-gradient-to-t from-[#065F46] to-[#10B981] cursor-pointer ${isActive ? 'ring-2 ring-emerald-300' : ''}`}
                            style={{ height: `${barHeight}%` }}
                            data-testid={`charges-chart-bar-${index}`}
                            title={`${point.label} • ${Number(point.amount || 0).toFixed(2)} € • ${Number(point.count || 0)} charge(s)`}
                            onClick={() => handleSelectBar(index)}
                          />
                        </div>
                        <p className="text-[10px] text-slate-500" data-testid={`charges-chart-label-${index}`}>{point.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {barDetailLoading && (
                <div className="py-4 flex items-center justify-center" data-testid="charges-bar-detail-loading">
                  <Loader2 className="w-5 h-5 animate-spin text-[#065F46]" />
                </div>
              )}

              {!barDetailLoading && barDetail && (
                <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3" data-testid="charges-bar-detail-card">
                  <div>
                    <p className="text-xs uppercase text-slate-500" data-testid="charges-bar-detail-title">Détail période {barDetail.label}</p>
                    <p className="text-xs text-slate-400" data-testid="charges-bar-detail-range">
                      {new Date(barDetail.start_date).toLocaleDateString('fr-FR')} - {new Date(barDetail.end_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-50 rounded p-2" data-testid="charges-bar-detail-count-card">
                      <p className="text-xs text-slate-500">Charges</p>
                      <p className="font-semibold text-slate-900" data-testid="charges-bar-detail-count-value">{barDetail.total_charges}</p>
                    </div>
                    <div className="bg-slate-50 rounded p-2" data-testid="charges-bar-detail-amount-card">
                      <p className="text-xs text-slate-500">Montant</p>
                      <p className="font-semibold text-[#065F46]" data-testid="charges-bar-detail-amount-value">{Number(barDetail.total_amount || 0).toFixed(2)} €</p>
                    </div>
                  </div>

                  <div className="space-y-1" data-testid="charges-bar-detail-payment-types">
                    <p className="text-xs uppercase text-slate-500">Modes de paiement</p>
                    {(barDetail.payment_types || []).slice(0, 6).map((paymentType, index) => (
                      <div key={`${paymentType.payment_type}-${index}`} className="text-xs flex items-center justify-between" data-testid={`charges-bar-detail-payment-type-row-${index}`}>
                        <span className="text-slate-700">{paymentType.payment_type} ({paymentType.count})</span>
                        <span className="font-mono text-slate-900">{Number(paymentType.amount || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.payment_types || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="charges-bar-detail-payment-types-empty">Aucun mode de paiement sur cette période</p>
                    )}
                  </div>

                  <div className="space-y-1" data-testid="charges-bar-detail-list">
                    {(barDetail.charges || []).slice(0, 12).map((charge, index) => (
                      <div key={`${charge.id}-${index}`} className="text-xs flex items-center justify-between gap-2 border-b border-slate-100 py-1" data-testid={`charges-bar-detail-row-${index}`}>
                        <span className="text-slate-700 truncate">{charge.description} • {charge.payment_type}</span>
                        <span className="font-mono text-slate-900">{Number(charge.amount || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.charges || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="charges-bar-detail-empty">Aucune charge sur cette période</p>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2" data-testid="charges-payment-breakdown">
                <div className="flex items-center gap-2 text-slate-600 text-xs uppercase font-medium">
                  <CreditCard className="w-4 h-4" />
                  <span data-testid="charges-payment-breakdown-title">Par type de paiement</span>
                </div>
                {paymentTypes.length === 0 ? (
                  <p className="text-sm text-slate-400 italic" data-testid="charges-payment-breakdown-empty">Aucune donnée</p>
                ) : (
                  <div className="space-y-2">
                    {paymentTypes.map((paymentType, index) => (
                      <div key={`${paymentType.payment_type}-${index}`} className="flex items-center justify-between text-sm" data-testid={`charges-payment-breakdown-row-${index}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${PAYMENT_COLORS[paymentType.payment_type] || 'bg-slate-500'}`} />
                          <span className="text-slate-700">{paymentType.payment_type}</span>
                          <span className="text-slate-400">({paymentType.count})</span>
                        </div>
                        <span className="font-mono text-slate-900" data-testid={`charges-payment-breakdown-amount-${index}`}>{Number(paymentType.amount || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-slate-200" data-testid="charges-list-card">
        <CardHeader className="border-b border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="font-outfit text-lg" data-testid="charges-list-title">
                Liste des charges
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5 capitalize" data-testid="charges-list-period-label">
                {listLabel}
              </p>
            </div>
            {barDetail ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearBarSelection}
                data-testid="charges-list-reset-button"
              >
                Revenir au mois en cours
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {filteredCharges.length === 0 ? (
            <div className="text-center py-10" data-testid="charges-empty-state">
              <Wallet className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">
                {barDetail ? 'Aucune charge sur la période sélectionnée' : 'Aucune charge ce mois-ci'}
              </p>
              {!barDetail ? (
                <p className="text-xs text-slate-400 mt-1">Appuyez sur une barre du diagramme pour voir d'autres périodes.</p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2" data-testid="charges-list">
              {filteredCharges.map((charge, index) => (
                <div key={charge.id} className="border border-slate-200 rounded-md p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`charges-list-row-${index}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 break-words" data-testid={`charges-list-description-${index}`}>{charge.description}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                      <span data-testid={`charges-list-date-${index}`}>{charge.payment_date ? format(parseISO(charge.payment_date), 'dd/MM/yyyy') : '-'}</span>
                      <span className="px-2 py-0.5 bg-slate-100 rounded" data-testid={`charges-list-type-${index}`}>{charge.payment_type}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {charge.photo_url ? (
                      <button
                        type="button"
                        onClick={() => setPhotoPreviewOpen(charge.photo_url)}
                        className="h-10 w-10 rounded border border-slate-200 overflow-hidden bg-slate-50 hover:ring-2 hover:ring-[#065F46] transition"
                        data-testid={`charges-list-photo-${index}`}
                        title="Voir le justificatif"
                      >
                        <img src={charge.photo_url} alt="Justificatif" className="w-full h-full object-cover" />
                      </button>
                    ) : null}
                    <span className="font-mono font-semibold text-[#065F46]" data-testid={`charges-list-amount-${index}`}>{Number(charge.amount || 0).toFixed(2)} €</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => openEditChargeDialog(charge)}
                      data-testid={`charges-list-edit-${index}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="text-red-600"
                      onClick={() => handleDeleteCharge(charge.id)}
                      data-testid={`charges-list-delete-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={chargeDialogOpen} onOpenChange={setChargeDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="charge-dialog">
          <DialogHeader>
            <DialogTitle data-testid="charge-dialog-title">{editingCharge ? 'Modifier la charge' : 'Ajouter une charge'}</DialogTitle>
            <DialogDescription data-testid="charge-dialog-description">Renseigne le descriptif, le montant et le type de paiement.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveCharge} className="space-y-4" data-testid="charge-form">
            <div className="space-y-2">
              <Label htmlFor="charge_description">Descriptif de paiement *</Label>
              <Input
                id="charge_description"
                value={chargeForm.description}
                onChange={(event) => setChargeForm({ ...chargeForm, description: event.target.value })}
                placeholder="Ex: Achat matériel"
                data-testid="charge-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="charge_amount">Montant (€) *</Label>
                <Input
                  id="charge_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={chargeForm.amount}
                  onChange={(event) => setChargeForm({ ...chargeForm, amount: event.target.value })}
                  data-testid="charge-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="charge_payment_type">Type de paiement *</Label>
                <Select
                  value={chargeForm.payment_type}
                  onValueChange={(value) => setChargeForm({ ...chargeForm, payment_type: value })}
                >
                  <SelectTrigger id="charge_payment_type" data-testid="charge-payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="charge_payment_date">Date de paiement *</Label>
              <Input
                id="charge_payment_date"
                type="date"
                value={chargeForm.payment_date}
                onChange={(event) => setChargeForm({ ...chargeForm, payment_date: event.target.value })}
                data-testid="charge-payment-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Justificatif (ticket, facture…)</Label>
              {chargeForm.photo_url ? (
                <div className="relative rounded-md border border-slate-200 overflow-hidden" data-testid="charge-photo-preview">
                  <img src={chargeForm.photo_url} alt="Justificatif" className="w-full max-h-48 object-contain bg-slate-50" />
                  <button
                    type="button"
                    onClick={() => setChargeForm({ ...chargeForm, photo_url: '' })}
                    className="absolute top-2 right-2 bg-white/95 hover:bg-white text-red-600 text-xs font-medium px-2 py-1 rounded shadow"
                    data-testid="charge-photo-remove"
                  >
                    Supprimer
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 px-3 py-2 rounded border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer text-sm text-slate-600" data-testid="charge-photo-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="charge-photo-input"
                  />
                  <span>Choisir une image (max 5 Mo)</span>
                </label>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" data-testid="charge-cancel-button">Annuler</Button>
              </DialogClose>
              <Button type="submit" className="bg-[#065F46] hover:bg-[#047857] text-white" data-testid="charge-submit-button">
                {editingCharge ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {photoPreviewOpen ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setPhotoPreviewOpen(null)}
          data-testid="charge-photo-fullscreen"
        >
          <img src={photoPreviewOpen} alt="Justificatif" className="max-w-full max-h-full rounded shadow-2xl" />
        </div>
      ) : null}
    </div>
  );
};
